import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWebSocket } from '../../context/WebSocketContext';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  SignalIcon,
  ServerIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const StatusIndicator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    database: 'checking',
    api: 'checking',
    websocket: 'checking',
    notifications: 'checking'
  });
  const { socket, isConnected } = useWebSocket();

  useEffect(() => {
    // Check system status on mount
    checkSystemStatus();
    
    // Check status every 30 seconds
    const interval = setInterval(checkSystemStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Update WebSocket status
    setSystemStatus(prev => ({
      ...prev,
      websocket: isConnected ? 'online' : 'offline'
    }));
  }, [isConnected]);

  const checkSystemStatus = async () => {
    try {
      // Check API status
      const response = await fetch('/api/health');
      if (response.ok) {
        const data = await response.json();
        setSystemStatus(prev => ({ 
          ...prev, 
          api: 'online',
          database: data.status === 'ok' ? 'online' : 'offline'
        }));
      } else {
        setSystemStatus(prev => ({ ...prev, api: 'offline' }));
      }
    } catch (error) {
      console.error('Health check error:', error);
      setSystemStatus(prev => ({ 
        ...prev, 
        api: 'offline',
        database: 'offline'
      }));
    }

    // Check notification service (simplified)
    setSystemStatus(prev => ({ ...prev, notifications: 'online' }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'offline':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'checking':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-600';
      case 'offline':
        return 'text-red-600';
      case 'checking':
        return 'text-yellow-600';
      default:
        return 'text-yellow-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'checking':
        return 'Checking...';
      default:
        return 'Unknown';
    }
  };

  const allOnline = Object.values(systemStatus).every(status => status === 'online');

  return (
    <div className="relative">
      {/* Status Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-full transition-colors ${
          allOnline ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
        } hover:bg-opacity-80`}
      >
        <SignalIcon className="h-5 w-5" />
        {!allOnline && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></span>
        )}
      </button>

      {/* Status Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900">System Status</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                {/* Database Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ServerIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Database</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(systemStatus.database)}
                    <span className={`text-xs font-medium ${getStatusColor(systemStatus.database)}`}>
                      {getStatusText(systemStatus.database)}
                    </span>
                  </div>
                </div>

                {/* API Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ServerIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">API Server</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(systemStatus.api)}
                    <span className={`text-xs font-medium ${getStatusColor(systemStatus.api)}`}>
                      {getStatusText(systemStatus.api)}
                    </span>
                  </div>
                </div>

                {/* WebSocket Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <SignalIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">WebSocket</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(systemStatus.websocket)}
                    <span className={`text-xs font-medium ${getStatusColor(systemStatus.websocket)}`}>
                      {getStatusText(systemStatus.websocket)}
                    </span>
                  </div>
                </div>

                {/* Notification Service */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BellIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Notifications</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(systemStatus.notifications)}
                    <span className={`text-xs font-medium ${getStatusColor(systemStatus.notifications)}`}>
                      {getStatusText(systemStatus.notifications)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Overall Status */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">Overall Status</span>
                  <div className="flex items-center space-x-2">
                    {allOnline ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className={`text-xs font-medium ${allOnline ? 'text-green-600' : 'text-yellow-600'}`}>
                      {allOnline ? 'All Systems Operational' : 'Some Issues Detected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Last Updated */}
              <div className="mt-2 text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </div>

              {/* Test Connection Button */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    try {
                      if (socket && socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
                        toast.success('Test message sent successfully');
                      } else {
                        toast.error('WebSocket not connected');
                      }
                    } catch (error) {
                      console.error('Test connection error:', error);
                      toast.error('Test connection failed');
                    }
                  }}
                  className="w-full px-3 py-2 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Test Connection
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StatusIndicator; 